import { Button } from "@/components/core/buttons/Button";
import { BookmarkIconButton } from "@/components/core/inputs/BookmarkIconButton";
import { cn } from "@/lib/utils";
import { Client, isFullBlock, isFullPage } from "@notionhq/client";
import {
    BlockObjectResponse,
    GetPageResponse,
    ListBlockChildrenResponse,
    ParagraphBlockObjectResponse,
    RichTextItemResponse
} from "@notionhq/client/build/src/api-endpoints";
import { IconBox } from "components/core/IconBox";
import { Header } from "components/core/templates/Header";
import {
    DesktopContentCTACard,
    MobileContentCTACard
} from "components/domain/contents/ContentCTACard";
import { ContentCommentSection } from "components/domain/contents/ContentCommentSection";
import { ContentPreview } from "components/domain/contents/ContentPreview";
import {
    MixContentByPkDocument,
    MixOriginalContentsDocument,
    Mix_Content_Category_Enum,
    useMixContentByPkQuery,
    useProductByPkQuery
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { useBookmark } from "hooks/useBookmark";
import { useContentReaction } from "hooks/useContentReaction";
import { useScrolled } from "hooks/useScrolled";
import { motion } from "framer-motion";
import {
    BookmarkIcon,
    CheckIcon,
    HeartIcon,
    MessageCircleIcon,
    PlusIcon,
    Share2Icon
} from "lucide-react";
import { AppCategory } from "models/AppCategory";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import Script from "next/script";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getClient } from "utils/client";
import { EXTENSION_STORE_URLS } from "utils/enum";

type Data = {
    content: NonNullable<MixContentByPkQuery["mix_content_by_pk"]>;
    page: GetPageResponse;
    children: ListBlockChildrenResponse;
    recordMap: {
        [key: string]: BlockObjectResponse[];
    };
    lastSSGTime: number;
};

/**
 * Content-specific background colors (temporary solution)
 * TODO: Replace with backend flag (e.g., custom_background_color field)
 */
const CONTENT_BACKGROUND_COLORS: Record<string, string> = {
    "ce29fb22-27a1-4acc-8ae0-6e1adad918e9": "#5B89FF"
};

const RichText = ({ text }: { text: RichTextItemResponse }) => {
    const className = useMemo(() => {
        const classes: string[] = [];

        if (text.annotations.bold) {
            classes.push("font-bold");
        }

        if (text.annotations.italic) {
            classes.push("italic");
        }

        if (text.annotations.strikethrough) {
            classes.push("line-through");
        }

        if (text.annotations.underline && !text.href) {
            classes.push("underline");
        }

        if (text.annotations.color !== "default") {
            // Notion color values can be named colors (e.g., "blue", "red", "gray")
            // or CSS color values (e.g., "#ff0000", "rgba(255,0,0,1)")
            const colorMap: Record<string, string> = {
                gray: "text-grey-500",
                brown: "text-amber-700",
                orange: "text-orange-500",
                yellow: "text-yellow-500",
                green: "text-green-500",
                blue: "text-blue-500",
                purple: "text-purple-500",
                pink: "text-pink-500",
                red: "text-red-500"
            };
            const colorClass = colorMap[text.annotations.color];
            if (colorClass) {
                classes.push(colorClass);
            } else {
                // For CSS color values or unknown named colors, use arbitrary value
                // Notion API may return CSS color strings directly
                classes.push(`text-[${text.annotations.color}]`);
            }
        }

        return classes.length > 0 ? cn(...classes) : undefined;
    }, [text.annotations, text.href]);

    const linkClassName =
        "border-b-[0.05em] border-[rgba(55,53,47,0.4)] opacity-70";

    if (!className) {
        return text.href ? (
            <a
                href={text.href}
                target="_blank"
                rel="noreferrer"
                className={linkClassName}
            >
                {text.plain_text}
            </a>
        ) : (
            <>{text.plain_text}</>
        );
    }

    return (
        <span className={className}>
            {text.href ? (
                <a
                    href={text.href}
                    target="_blank"
                    rel="noreferrer"
                    className={linkClassName}
                >
                    {text.plain_text}
                </a>
            ) : (
                <>{text.plain_text}</>
            )}
        </span>
    );
};

const Page = ({
    content: ssrContent,
    page,
    children,
    recordMap,
    lastSSGTime
}: Data) => {
    const router = useRouter();

    const { me } = useAuth();
    const scrolled = useScrolled(0);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { data } = useMixContentByPkQuery({
        fetchPolicy: "cache-and-network",
        variables: {
            id: ssrContent.id
        }
    });

    const blocks = useMemo(
        () => children.results.filter(isFullBlock),
        [children]
    );

    const content = useMemo(
        () => data?.mix_content_by_pk ?? ssrContent,
        [data?.mix_content_by_pk, ssrContent]
    );

    const contentInfo = useMemo(
        () => AppCategory.toContentInfo(content),
        [content]
    );

    const productId =
        (router.query.product_id as string) || content.default_product_id;

    const { data: productData } = useProductByPkQuery({
        fetchPolicy: "cache-and-network",
        variables: { id: productId! },
        skip: !productId
    });

    const product = useMemo(() => productData?.product_by_pk, [productData]);

    const showCTACard = useMemo(
        () =>
            content.category === Mix_Content_Category_Enum.Offline ||
            content.category === Mix_Content_Category_Enum.Report ||
            product,
        [content.category, product]
    );

    const ctaText = useMemo(
        () =>
            content.category === Mix_Content_Category_Enum.Offline
                ? "신청하기"
                : content.category === Mix_Content_Category_Enum.Report
                  ? "리포트 다운로드"
                  : "상품 결제하기",
        [content.category]
    );

    const { toggle: toggleBookmark } = useBookmark(content);
    const isBookmarked = !!content.bookmarked;
    const {
        toggle: toggleReaction,
        isReacted,
        reactionCount
    } = useContentReaction(content);
    const commentSectionRef = useRef<HTMLDivElement>(null);
    const [isCopied, setIsCopied] = useState(false);

    // 댓글 섹션으로 스크롤
    const scrollToComments = useCallback(() => {
        commentSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    }, []);

    const findStreakIndex = useCallback(
        (
            siblings: BlockObjectResponse[],
            blockId: string,
            isStreak: (block: BlockObjectResponse) => boolean
        ) => {
            let index = siblings.findIndex((block) => block.id === blockId);

            if (index === -1) {
                return null;
            }

            let i = 0;
            let sibling: BlockObjectResponse | undefined;

            while (
                index-- > 0 &&
                (sibling = siblings[index]) &&
                isStreak(sibling)
            ) {
                i++;
            }

            return i;
        },
        []
    );

    const renderBlock = useCallback(
        (block: BlockObjectResponse) => {
            if (block.type === "paragraph") {
                return (
                    <div
                        className="w-full max-w-full whitespace-pre-wrap
                            break-words px-0.5 py-[3px] empty:h-5"
                    >
                        {block.paragraph.rich_text.map((text, index) => (
                            <RichText key={index} text={text} />
                        ))}
                    </div>
                );
            } else if (block.type === "heading_1") {
                return (
                    <h2
                        className="mb-1 mt-8 px-0.5 py-[3px] text-[1.875em]
                            font-bold leading-[1.3] md:text-2xl"
                    >
                        <div
                            className="w-full max-w-full whitespace-pre-wrap
                                break-words px-0.5 py-[3px] empty:h-5"
                        >
                            {block.heading_1.rich_text.map((text, index) => (
                                <RichText key={index} text={text} />
                            ))}
                        </div>
                    </h2>
                );
            } else if (block.type === "heading_2") {
                return (
                    <h3
                        className="mb-[1px] mt-[1.4em] px-0.5 py-[3px]
                            text-[1.5em] font-bold leading-[1.3] md:text-[22px]"
                    >
                        {block.heading_2.rich_text.map((text, index) => (
                            <RichText key={index} text={text} />
                        ))}
                    </h3>
                );
            } else if (block.type === "heading_3") {
                return (
                    <h4
                        className="mb-0 mt-4 px-0.5 py-[3px] text-[1.25em]
                            font-bold leading-[1.5] md:text-xl"
                    >
                        {block.heading_3.rich_text.map((text, index) => (
                            <RichText key={index} text={text} />
                        ))}
                    </h4>
                );
            } else if (block.type === "bulleted_list_item") {
                return (
                    <div className="my-[1px]">
                        <div className="flex">
                            <div
                                className="before:text-[1.5em]
                                    before:leading-none before:content-['•']"
                            />
                            <div className="flex-1">
                                {block.bulleted_list_item.rich_text.map(
                                    (text, index) => (
                                        <RichText key={index} text={text} />
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                );
            } else if (block.type === "numbered_list_item") {
                const streakIndex = findStreakIndex(
                    blocks,
                    block.id,
                    (b) => b.type === "numbered_list_item"
                );

                if (streakIndex === null) {
                    return null;
                }

                return (
                    <div className="my-[1px]">
                        <div className="flex">
                            <div className="w-6 whitespace-nowrap text-center">
                                {streakIndex + 1}.
                            </div>
                            <div className="flex-1">
                                {block.numbered_list_item.rich_text.map(
                                    (text, index) => (
                                        <RichText key={index} text={text} />
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                );
            } else if (block.type === "quote") {
                return (
                    <div className="my-1">
                        <div
                            className="flex fill-inherit px-0.5 py-[3px]
                                text-base"
                        >
                            <div
                                className="w-full border-l-[3px]
                                    border-l-current pl-3.5 pr-3.5"
                            >
                                {block.quote.rich_text.map((text, index) => (
                                    <RichText key={index} text={text} />
                                ))}
                            </div>
                        </div>
                    </div>
                );
            } else if (block.type === "divider") {
                return (
                    <div className="my-[1px]">
                        <hr
                            className="h-px w-full border-none
                                bg-component-divider-line"
                        />
                    </div>
                );
            } else if (block.type === "image") {
                const imageUrl =
                    block.image.type === "external"
                        ? block.image.external.url
                        : block.image.file.url;

                // Special case: wrap specific image with clickable link
                const isSpecialImage =
                    content.id === "f5d30b4d-b2ce-4f23-b4d9-78b7b8f04b68" &&
                    imageUrl.includes("859fffbb-dce9-48ce-b54a-b7e483337ac8");

                const imageElement = (
                    <div className="relative h-auto w-full">
                        <Image
                            className="w-full"
                            alt={
                                block.image.caption.length > 0
                                    ? block.image.caption
                                          .map((t) => t.plain_text)
                                          .join("")
                                    : "content image"
                            }
                            src={imageUrl}
                            width={800}
                            height={600}
                            style={{ width: "100%", height: "auto" }}
                            unoptimized={
                                imageUrl.includes("notion.so") ||
                                imageUrl.includes("s3.us-west")
                            }
                        />
                    </div>
                );

                return (
                    <div className="my-[1px]">
                        <div>
                            {isSpecialImage ? (
                                <a
                                    href="https://www.metavv.com/ko/content/10937576"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block cursor-pointer"
                                >
                                    {imageElement}
                                </a>
                            ) : (
                                imageElement
                            )}
                            <div
                                className="w-full max-w-full whitespace-pre-wrap
                                    break-words pb-1.5 pl-0.5 pt-1.5 text-sm
                                    leading-[1.4]
                                    caret-[var(--color-text-secondary)]"
                            >
                                {block.image.caption.length > 0 &&
                                    block.image.caption.map((text, index) => (
                                        <RichText key={index} text={text} />
                                    ))}
                            </div>
                        </div>
                    </div>
                );
            } else if (block.type === "video") {
                return (
                    <div className="my-[1px]">
                        <video
                            className="w-full"
                            src={
                                block.video.type === "external"
                                    ? block.video.external.url
                                    : block.video.file.url
                            }
                            controls
                            preload="metadata"
                            playsInline
                        />
                    </div>
                );
            } else if (block.type === "callout") {
                const children = recordMap[block.id];

                const color = block.callout.color;

                return (
                    <div className="my-[1px]">
                        <div
                            className={cn(
                                `flex w-full rounded-[3px] p-4 pl-3
                                text-foreground`,
                                color === "default"
                                    ? `border border-component-divider-line
                                        bg-transparent`
                                    : "border-none bg-grey-100"
                            )}
                        >
                            {block.callout.icon && (
                                <div className="relative w-6">
                                    {block.callout.icon.type === "emoji" ? (
                                        block.callout.icon.emoji
                                    ) : block.callout.icon.type ===
                                      "external" ? (
                                        <Image
                                            alt="callout icon"
                                            src={
                                                block.callout.icon.external.url
                                            }
                                            width={24}
                                            height={24}
                                            unoptimized
                                        />
                                    ) : null}
                                </div>
                            )}
                            <div className="ml-2 flex w-full min-w-0 flex-col">
                                <div
                                    className="w-full max-w-full
                                        whitespace-pre-wrap break-words px-0.5
                                        caret-[var(--color-text-primary)]"
                                >
                                    {block.callout.rich_text.length > 0 &&
                                        block.callout.rich_text.map(
                                            (text, index) => (
                                                <RichText
                                                    key={index}
                                                    text={text}
                                                />
                                            )
                                        )}
                                </div>
                                {children &&
                                    children.map((child) => renderBlock(child))}
                            </div>
                        </div>
                    </div>
                );
            }
        },
        [findStreakIndex, blocks, recordMap]
    );

    /** 노션 이미지 만료 해결을 위한 캐시 비활성화
     * https://github.com/teammint-tech/mix/pull/223
     */
    useEffect(() => {
        if (lastSSGTime + 1000 * 60 * 60 <= Date.now()) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            } else {
                timeoutRef.current = setTimeout(() => {
                    router.replace(router.asPath, undefined, {
                        unstable_skipClientCache: true,
                        scroll: false
                    });
                }, 2000);
            }
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [lastSSGTime, router]);

    const [browserName, setBrowserName] = useState("");
    const isChromiumBased = ["Google Chrome", "Chromium"];

    useEffect(() => {
        setBrowserName(
            (() => {
                if (!navigator.userAgentData) {
                    return "Unknown";
                }

                const current =
                    navigator.userAgentData.brands?.find(({ brand }) =>
                        [
                            "Firefox",
                            "Safari",
                            "Whale",
                            "Opera",
                            "Edge"
                        ].includes(brand)
                    )?.brand || "Unknown";

                return current === "Unknown" &&
                    navigator.userAgentData.brands?.find(({ brand }) =>
                        isChromiumBased.includes(brand)
                    )
                    ? "Chrome"
                    : current;
            })()
        );
    }, []);

    // Crisp 챗봇 숨김 처리 (특정 콘텐츠 페이지)
    useEffect(() => {
        if (content.id === "f5d30b4d-b2ce-4f23-b4d9-78b7b8f04b68") {
            // Crisp 챗봇 숨김
            (window as any)?.$crisp?.push(["do", "chat:hide"]);
        }

        return () => {
            // 페이지 이탈 시 Crisp 챗봇 다시 표시
            if (content.id === "f5d30b4d-b2ce-4f23-b4d9-78b7b8f04b68") {
                (window as any)?.$crisp?.push(["do", "chat:show"]);
            }
        };
    }, [content.id]);

    // 공유하기 핸들러 (클립보드에 URL 복사)
    const handleShare = useCallback(async () => {
        const shareUrl = `${window.location.origin}/contents/original/${content.id}`;
        await navigator.clipboard.writeText(shareUrl);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    }, [content.id]);

    if (isFullPage(page)) {
        return (
            <>
                <Script
                    src="https://developers.kakao.com/sdk/js/kakao.min.js"
                    strategy="lazyOnload"
                    onLoad={() => {
                        if (
                            typeof window !== "undefined" &&
                            window.Kakao &&
                            !window.Kakao.isInitialized()
                        ) {
                            window.Kakao.init(
                                process.env.NEXT_PUBLIC_KAKAO_JAVASCRIPT_API_KEY
                            );
                        }
                    }}
                />
                <Head>
                    <title>{`${content.title} | 믹스(Mix)`}</title>
                    <meta
                        name="description"
                        content={
                            content.abstractcontents ||
                            "Mix 오리지널 콘텐츠 - 마케팅 인사이트를 담은 깊이 있는 콘텐츠"
                        }
                    />
                    <link
                        rel="canonical"
                        href={`https://mix.day/contents/original/${content.id}`}
                    />

                    {/* Open Graph */}
                    <meta property="og:type" content="article" />
                    <meta property="og:title" content={content.title} />
                    <meta
                        property="og:description"
                        content={
                            content.abstractcontents || "Mix 오리지널 콘텐츠"
                        }
                    />
                    <meta
                        property="og:url"
                        content={`https://mix.day/contents/original/${content.id}`}
                    />
                    {(content.cover_imgurl || content.imgurl) && (
                        <>
                            <meta
                                key="og:image"
                                property="og:image"
                                content={
                                    content.cover_imgurl || content.imgurl || ""
                                }
                            />
                            <meta property="og:image:width" content="1200" />
                            <meta property="og:image:height" content="630" />
                        </>
                    )}
                    {content.authorname && (
                        <meta
                            property="article:author"
                            content={content.authorname}
                        />
                    )}
                    {content.updatetime && (
                        <meta
                            property="article:published_time"
                            content={new Date(content.updatetime).toISOString()}
                        />
                    )}

                    {/* Twitter Card */}
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={content.title} />
                    <meta
                        name="twitter:description"
                        content={
                            content.abstractcontents || "Mix 오리지널 콘텐츠"
                        }
                    />
                    {(content.cover_imgurl || content.imgurl) && (
                        <meta
                            name="twitter:image"
                            content={
                                content.cover_imgurl || content.imgurl || ""
                            }
                        />
                    )}

                    {/* Structured Data (JSON-LD) */}
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify({
                                "@context": "https://schema.org",
                                "@type": "Article",
                                headline: content.title,
                                description:
                                    content.abstractcontents ||
                                    "Mix 오리지널 콘텐츠",
                                image:
                                    content.cover_imgurl ||
                                    content.imgurl ||
                                    "",
                                datePublished: content.updatetime
                                    ? new Date(content.updatetime).toISOString()
                                    : undefined,
                                dateModified: content.updatetime
                                    ? new Date(content.updatetime).toISOString()
                                    : undefined,
                                author: content.authorname
                                    ? {
                                          "@type": "Person",
                                          name: content.authorname
                                      }
                                    : {
                                          "@type": "Organization",
                                          name: "팀민트(Team Mint)"
                                      },
                                publisher: {
                                    "@type": "Organization",
                                    name: "믹스(Mix)",
                                    logo: {
                                        "@type": "ImageObject",
                                        url: "https://mix.day/img/ogImage.png"
                                    }
                                },
                                mainEntityOfPage: {
                                    "@type": "WebPage",
                                    "@id": `https://mix.day/contents/original/${content.id}`
                                }
                            })
                        }}
                    />
                </Head>
                <div
                    className="mx-auto box-border w-max
                        bg-[var(--color-background)] outline-none"
                >
                    <Header
                        className="fixed left-0 top-0 flex
                            h-[var(--header-height)] w-full items-center
                            justify-between transition-[300ms] md:p-0"
                        type={scrolled ? "border" : "transparent"}
                        showSearch={true}
                        hideAuth={false}
                    >
                        <div className="mr-[var(--radius-medium)]">
                            <BookmarkIconButton
                                variant={scrolled ? "filled" : "icon"}
                                checked={!!content.bookmarked}
                                onChange={toggleBookmark}
                            />
                        </div>
                    </Header>

                    <div
                        className="absolute left-0 right-0 top-0 h-[340px]
                            w-full overflow-hidden
                            md:h-[var(--content-thumbnail-height)]"
                        style={{
                            backgroundColor:
                                CONTENT_BACKGROUND_COLORS[content.id] ||
                                undefined
                        }}
                    >
                        {!CONTENT_BACKGROUND_COLORS[ssrContent.id] &&
                            (ssrContent.cover_imgurl || ssrContent.imgurl) && (
                                <>
                                    <Image
                                        src={
                                            ssrContent.cover_imgurl ||
                                            ssrContent.imgurl ||
                                            ""
                                        }
                                        alt={ssrContent.title}
                                        fill
                                        preload
                                        className="object-cover object-center"
                                        sizes="100vw"
                                        unoptimized={
                                            (
                                                ssrContent.cover_imgurl ||
                                                ssrContent.imgurl ||
                                                ""
                                            ).includes("notion.so") ||
                                            (
                                                ssrContent.cover_imgurl ||
                                                ssrContent.imgurl ||
                                                ""
                                            ).includes("s3.us-west")
                                        }
                                    />
                                    <div
                                        className="absolute inset-0
                                            bg-gradient-to-b from-transparent
                                            to-[var(--scrim)]"
                                    />
                                </>
                            )}
                    </div>

                    <section
                        className="relative z-[1] flex h-[340px] w-screen
                            max-w-[var(--content-body-width)] flex-col
                            justify-end px-6 py-8
                            md:h-[var(--content-thumbnail-height)] md:px-5
                            md:py-20"
                    >
                        <h1
                            className="mb-4 text-[24px] font-bold text-white
                                sm:max-w-none md:mb-10 md:text-[40px]"
                        >
                            {content.title}
                        </h1>
                        <div className="flex w-full max-w-[480px]">
                            {contentInfo.map(({ title, content }, index) => (
                                <div
                                    key={title}
                                    className={cn(
                                        "flex flex-col md:max-w-[130px]",
                                        index !== contentInfo.length - 1 &&
                                            "mr-6 md:mr-4"
                                    )}
                                >
                                    <div
                                        className="whitespace-nowrap text-sm
                                            text-[#bdbdbe]"
                                    >
                                        {title}
                                    </div>
                                    <div className="font-medium text-white">
                                        {content}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section
                        className="flex max-w-[100vw] select-none gap-0
                            px-[var(--notion-page-padding)] py-8 text-base
                            leading-[1.8] md:gap-4 md:px-5 md:py-20"
                    >
                        <div
                            className="w-full max-w-[var(--content-body-width)]"
                        >
                            {/* TODO: 지호 님 인터뷰 콘텐츠 섭외 종료되면, content.contentid === 말랑말랑 오리지날 컨텐츠 삭제하기! */}
                            {(content.category &&
                                AppCategory.categories[content.category]
                                    .public) ||
                            me ||
                            content.contentid ===
                                "b827047e-1394-41ce-9dd6-0afa1b189d16" ||
                            "81edf800-86e4-465a-a029-32cb70e98241" ? (
                                blocks.map((block) => renderBlock(block))
                            ) : (
                                <ContentPreview content={content}>
                                    {blocks
                                        .slice(
                                            0,
                                            Math.floor(blocks.length * 0.4)
                                        )
                                        .map((block) => renderBlock(block))}
                                </ContentPreview>
                            )}
                            {showCTACard ? (
                                <MobileContentCTACard
                                    content={content}
                                    contentInfo={contentInfo}
                                    product={product ?? null}
                                    ctaText={ctaText}
                                    onShareClick={handleShare}
                                    browserName={browserName}
                                />
                            ) : (
                                <></>
                            )}
                            <div
                                className="mt-8 hidden items-center
                                    justify-center gap-4 text-foreground
                                    md:ml-auto md:flex md:w-full md:items-start"
                            >
                                {/* <Button
                                    variant="filled"
                                    color="secondary"
                                    className="flex-1 px-3 py-2"
                                    onClick={handleShare}
                                >
                                    <Share2Icon className="mr-1 size-4" />
                                    콘텐츠 공유하기
                                </Button> */}
                                <Button
                                    variant="filled"
                                    className={cn(
                                        "flex-1 px-3 py-2",
                                        ["Safari", "Opera", "Unknown"].includes(
                                            browserName
                                        ) && "hidden"
                                    )}
                                    onClick={() => {
                                        window.open(
                                            EXTENSION_STORE_URLS[
                                                browserName.toUpperCase()
                                            ]?.url,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <PlusIcon className="mr-1 size-4 text-white" />
                                    {browserName}에 추가하고 인사이트 챙기기
                                </Button>
                            </div>
                        </div>
                        {showCTACard ? (
                            <DesktopContentCTACard
                                content={content}
                                contentInfo={contentInfo}
                                product={product ?? null}
                                ctaText={ctaText}
                            />
                        ) : (
                            <></>
                        )}
                    </section>

                    {/* 액션 버튼 + 댓글 섹션 */}
                    <section
                        className="mx-auto w-full
                            max-w-[var(--content-body-width)]
                            px-[var(--notion-page-padding)] pb-20 md:px-5"
                    >
                        {/* 액션 버튼 */}
                        <div
                            className="flex justify-center gap-4 border-y
                                border-grey-200 py-5 dark:border-grey-300"
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={toggleReaction}
                                className={cn(
                                    `flex items-center gap-2 rounded-md p-2
                                    transition-all duration-300
                                    hover:bg-gray-200 dark:hover:bg-gray-800`,
                                    isReacted && "text-red-500"
                                )}
                            >
                                <HeartIcon
                                    className={cn(
                                        "size-5",
                                        isReacted &&
                                            "fill-current text-[#FF6B6B]"
                                    )}
                                />
                                <span>{reactionCount}</span>
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={scrollToComments}
                                className="flex items-center gap-2 rounded-md
                                    p-2 transition-all duration-300
                                    hover:bg-gray-200 dark:hover:bg-gray-800"
                            >
                                <MessageCircleIcon className="size-5" />
                                <span>{content.comment_count}</span>
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={toggleBookmark}
                                className={cn(
                                    `flex items-center gap-2 rounded-md p-2
                                    transition-all duration-300
                                    hover:bg-gray-200 dark:hover:bg-gray-800`,
                                    isBookmarked &&
                                        "text-[var(--subcolor-orange)]"
                                )}
                            >
                                <BookmarkIcon
                                    className={cn(
                                        "size-5",
                                        isBookmarked &&
                                            "fill-current text-orange-500"
                                    )}
                                />
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={handleShare}
                                className={cn(
                                    `flex items-center gap-2 rounded-md p-2
                                    transition-all duration-300
                                    hover:bg-gray-200 dark:hover:bg-gray-800`,
                                    isCopied && "text-green-500"
                                )}
                            >
                                {isCopied ? (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 500,
                                            damping: 15
                                        }}
                                    >
                                        <CheckIcon className="size-5" />
                                    </motion.div>
                                ) : (
                                    <Share2Icon className="size-5" />
                                )}
                            </Button>
                        </div>

                        {/* 댓글 섹션 */}
                        <div ref={commentSectionRef}>
                            <ContentCommentSection
                                hideTopDivider
                                contentId={content.id}
                            />
                        </div>
                    </section>
                </div>
            </>
        );
    }

    return <div />;
};

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const client = getClient({
            headers: {
                "x-hasura-admin-secret": process.env.HASURA_ADMIN_SECRET
            },
            ctx: null
        });

        const { data } = await client.query<
            MixOriginalContentsQuery,
            MixOriginalContentsQueryVariables
        >({
            query: MixOriginalContentsDocument
        });

        return {
            paths: data.mix_content.map((content) => ({
                params: { id: content.id }
            })),
            fallback: "blocking"
        };
    } catch {
        // API not reachable during build (e.g. Docker build environment)
        // Pages will be generated on-demand via ISR (fallback: blocking)
        return { paths: [], fallback: "blocking" };
    }
};

export const getStaticProps: GetStaticProps = async (ctx) => {
    const id = ctx.params?.id;

    if (typeof id !== "string") {
        throw new Error("id is not exists!");
    }

    const client = getClient({
        headers: {
            "x-hasura-admin-secret": process.env.HASURA_ADMIN_SECRET
        },
        ctx: null
    });

    let content: MixContentByPkQuery["mix_content_by_pk"];
    try {
        const response = await client.query<
            MixContentByPkQuery,
            MixContentByPkQueryVariables
        >({
            query: MixContentByPkDocument,
            variables: {
                id
            }
        });
        content = response.data.mix_content_by_pk;
    } catch (error) {
        // 데이터베이스 쿼리 실패 시 빌드 실패 방지 — ISR로 재시도
        console.error(`Failed to fetch content by pk ${id}:`, error);
        return { notFound: true, revalidate: 60 };
    }

    if (content?.source !== "original") {
        throw new Error("this is not original content!");
    }

    const notionClient = new Client({
        auth: process.env.NOTION_ORIGINAL_SECRET
    });

    // Notion 페이지 전체를 50초 이내에 가져오지 못하면 notFound로 처리 (빌드 타임아웃 방지)
    const fetchNotionData = async () => {
        const page = await notionClient.pages.retrieve({
            page_id: content.contentid
        });

        let children: ListBlockChildrenResponse = {
            type: "block",
            block: {},
            object: "list",
            results: [],
            has_more: true,
            next_cursor: null
        };

        while (children.has_more) {
            const $children = await notionClient.blocks.children.list({
                block_id: content.contentid,
                page_size: 100,
                start_cursor: children.next_cursor ?? undefined
            });

            children.results.push(...$children.results);
            children.has_more = $children.has_more;
            children.next_cursor = $children.next_cursor;
        }

        const result = await Promise.allSettled(
            children.results
                .filter(isFullBlock)
                .filter((block) => block.has_children)
                .map(
                    (block) =>
                        new Promise<{
                            [key: string]: Array<BlockObjectResponse>;
                        }>(async (resolve, reject) => {
                            try {
                                const { results } =
                                    await notionClient.blocks.children.list({
                                        block_id: block.id
                                    });
                                resolve({
                                    [block.id]: results.filter(isFullBlock)
                                });
                            } catch (error) {
                                console.warn(
                                    `Failed to fetch children for block ${block.id}:`,
                                    error
                                );
                                reject(error);
                            }
                        })
                )
        );

        const recordMap = result.reduce((prev, curr) => {
            if (curr.status === "fulfilled") {
                return { ...prev, ...curr.value };
            }
            return prev;
        }, {});

        return { page, children, recordMap };
    };

    try {
        let timeoutHandle: NodeJS.Timeout;
        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutHandle = setTimeout(
                () => reject(new Error(`Notion fetch timed out after 50s for page ${id}`)),
                50000
            );
        });

        const { page, children, recordMap } = await Promise.race([
            fetchNotionData(),
            timeoutPromise
        ]).finally(() => clearTimeout(timeoutHandle!));

        return {
            props: {
                content,
                page,
                children,
                recordMap,
                lastSSGTime: Date.now(),
                __APOLLO_CACHE_DATA__: client.extract()
            },
            revalidate: 60
        };
    } catch (error) {
        console.error(
            `Failed to fetch Notion content for page ${id} (contentid: ${content.contentid}):`,
            error
        );
        // notFound으로 처리해 빌드 실패 방지 — ISR(revalidate)로 재시도
        return {
            notFound: true,
            revalidate: 60
        };
    }
};

export default Page;
